import type { AgentsBoard, Proposal, ProposalKind, Recipient } from '../../api/agentSchemas';
import type { AgentLine } from './agentLine';
import type { FinanceFacts } from './finance/facts';
import { normalize } from './normalize';
import { replyFor } from './replies';
import { routeQuestion } from './router/route';
import { namedProposalKind } from './router/rules';
import { aboutKind, pendingOf } from './work';

export type { AgentLine, Figure } from './agentLine';
export { greeting } from './work';

/**
 * What the team says. v1 is deterministic on purpose: a typed question is
 * placed by the router (a tiny classifier trained on a corpus of owner questions,
 * backed by keywords, see router/) on one of the situations the team knows, and
 * `replyFor` says the recorded line of that situation. Anything unplanned lands
 * on the general answer of its topic, and anything else gets an honest "todavía
 * no". No generative model is called, so nothing a customer wrote can steer it.
 * Every line is one of the recordings in voice/lines.ts; what changes from one
 * business to the next travels beside it as a note or as figures.
 */

const MAX_PROPOSAL_CHIPS = 3;
const MAX_CHIPS = 4;
const MIN_PHONE_DIGITS = 8;

const CHIP: Readonly<Record<ProposalKind, (proposal: Proposal) => string>> = {
  offer: (proposal) => `Revisa la ${lowerFirst(proposal.title)}`,
  win_back: () => '¿Quién necesita seguimiento?',
  confirm_visits: () => 'Confirma las citas de mañana',
};

/** The first question of money is always there; the second changes with the weekday. */
const MONEY_CHIP = '¿Cuánto vendí este mes?';
const MONEY_CHIPS_BY_DAY: readonly string[] = [
  '¿Cuánto me queda?',
  '¿En qué gasto más?',
  '¿Cómo voy contra el mes pasado?',
  '¿Quién me debe?',
  '¿Cómo me pagan?',
  '¿Qué servicio me deja más?',
  '¿Cuánto cobré hoy?',
];

type SuggestionOptions = {
  /** The owner may read the numbers of the business, so two questions about money join the chips. */
  finance?: boolean;
  now?: Date;
};

export function suggestions(board: AgentsBoard, { finance = false, now = new Date() }: SuggestionOptions = {}): string[] {
  const pending = pendingOf(board);
  const money = finance ? [MONEY_CHIP, MONEY_CHIPS_BY_DAY[now.getDay() % MONEY_CHIPS_BY_DAY.length] ?? ''] : [];
  if (pending.length === 0) return ['¿Qué pueden hacer?', '¿Cómo va el negocio?', ...money];
  const chips = pending.map((proposal) => CHIP[proposal.kind](proposal));
  if (finance) return [...new Set(chips)].slice(0, MAX_PROPOSAL_CHIPS).concat(money);
  return [...new Set([...chips, '¿Qué hago primero?'])].slice(0, MAX_CHIPS);
}

const NO_NUMBERS: FinanceFacts = { status: 'error' };

/**
 * The reply to something typed. `finance` carries the numbers of the business
 * when the question needs them (see `financeNeedsFor`); without them a question
 * about money is answered with an honest failure, never a guess.
 */
export function answer(text: string, board: AgentsBoard, finance: FinanceFacts = NO_NUMBERS, now: Date = new Date()): AgentLine {
  const said = normalize(text);
  const chip = pendingOf(board).find((proposal) => normalize(CHIP[proposal.kind](proposal)) === said);
  if (chip) return aboutKind(board, chip.kind);
  return replyFor(routeQuestion(text).intent, { board, finance, now, named: namedProposalKind(text) });
}

/** The message as this one person gets it: their first name, and the hour and service of their visit. */
export function personalize(proposal: Proposal, recipient: Recipient): string {
  if (proposal.draft.kind !== 'message') return '';
  const [hora = '', servicio = ''] = recipient.detail.split(' · ');
  return proposal.draft.text
    .replaceAll('{nombre}', firstName(recipient.name))
    .replaceAll('{hora}', hora)
    .replaceAll('{servicio}', servicio);
}

/** A wa.me link with the text ready; the owner sends it from their own phone. */
export function whatsappLink(phone: string | null, text: string): string | null {
  const digits = phone?.replace(/\D/g, '') ?? '';
  if (digits.length < MIN_PHONE_DIGITS) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}
